/**
 * Fetches sizes configuration from CMS based on delivery key and current route
 *
 * @param {string} deliveryKey - Unique key to identify the specific sizes configuration
 * @returns {Promise<Object>} Fetch result containing sizes configuration
 *
 * @description
 * - Retrieves sizes configuration using CMS API
 * - Supports localization and optional query parameters
 * - Uses useFetch with caching mechanism
 *
 * @example
 * // Fetch sizes configuration for a specific delivery key
 * const { data } = await useSizesConfig('sizes-config')
 */
export async function useSizesConfig(deliveryKey: string) {
  const localePath = useLocalePath()

  /**
   * Query parameters for sizes configuration request
   * @type {Object}
   * @property {string} locale - Current localized path
   * @property {string} [vse] - Optional VSE query parameter
   * @property {string} [contentId] - Optional content ID query parameter
   * @property {string} deliveryKey - Key for specific sizes configuration
   */
  const query = {
    locale: localePath(''),
    vse: useRoute().query.vse,
    contentId: useRoute().query.contentId,
    deliveryKey,
  }

  // Fetch sizes configuration with caching
  return useFetch('/api/cms/getSizesConfig', {
    query,
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
  })
}
